import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowLeft, Loader2, RefreshCw, Send, Shield } from "lucide-react";
import { trpc } from "@/lib/trpc";

interface AccountSupportThreadProps {
  /** Only the canonical owner may use the coach endpoints. Never a recipient picker. */
  clientId?: number;
  clientName?: string;
  onBack: () => void;
  onReviewMembership?: () => void;
  onSignOut?: () => void;
  signingOut?: boolean;
}

/** Text-only account help. Intentionally independent of ordinary DM components. */
export function AccountSupportThread({
  clientId,
  clientName,
  onBack,
  onReviewMembership,
  onSignOut,
  signingOut,
}: AccountSupportThreadProps) {
  const isCoach = clientId !== undefined;
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState("");
  const [sendError, setSendError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const sending = useRef(false);
  const scrollArea = useRef<HTMLDivElement>(null);
  const followLatest = useRef(true);
  const lastReadAttempt = useRef("");
  const clientThread = trpc.accountSupport.thread.useQuery(undefined, {
    enabled: !isCoach,
    retry: false,
    refetchInterval: 10_000,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const coachThread = trpc.accountSupport.coachThread.useQuery(
    { clientId: clientId! },
    {
      enabled: isCoach,
      retry: false,
      refetchInterval: 10_000,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );
  const thread = isCoach ? coachThread : clientThread;
  const sendClient = trpc.accountSupport.send.useMutation({ retry: false });
  const sendCoach = trpc.accountSupport.coachSend.useMutation({ retry: false });
  const markClientRead = trpc.accountSupport.markRead.useMutation({
    retry: false,
  });
  const markCoachRead = trpc.accountSupport.coachMarkRead.useMutation({
    retry: false,
  });
  const isSending = sendClient.isPending || sendCoach.isPending;
  const messages = thread.data?.messages ?? [];

  useEffect(() => {
    const el = scrollArea.current;
    if (el && followLatest.current) el.scrollTop = el.scrollHeight;
  }, [thread.data]);

  useEffect(() => {
    const unread = messages.filter(
      (message) => message.fromCoach !== isCoach && !message.readAt,
    );
    const signature = unread.map((message) => message.id).join(",");
    if (!signature || signature === lastReadAttempt.current || thread.isError)
      return;
    lastReadAttempt.current = signature;
    const request = isCoach
      ? markCoachRead.mutateAsync({ clientId: clientId! })
      : markClientRead.mutateAsync();
    void request
      .then(() =>
        isCoach
          ? utils.accountSupport.coachThread.invalidate({ clientId: clientId! })
          : utils.accountSupport.thread.invalidate(),
      )
      .catch(() => {
        lastReadAttempt.current = "";
      });
    // Polling retries failed read receipts without affecting the conversation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.data, thread.dataUpdatedAt, thread.isError, clientId, isCoach]);

  async function handleSend(event: FormEvent) {
    event.preventDefault();
    const content = draft.trim();
    if (
      sending.current ||
      !content ||
      content.length > 4000 ||
      thread.isError ||
      !thread.data
    )
      return;
    sending.current = true;
    setSendError(null);
    setSent(false);
    try {
      if (isCoach)
        await sendCoach.mutateAsync({ clientId: clientId!, content });
      else await sendClient.mutateAsync({ content });
      setDraft("");
      setSent(true);
      followLatest.current = true;
      // The send is already committed. A failed refresh must not offer a resend.
      await thread.refetch().catch(() => undefined);
    } catch {
      setSendError(
        "Your message could not be sent. Your draft is still here. Please try again.",
      );
    } finally {
      sending.current = false;
    }
  }

  return (
    <section
      aria-label="Account support conversation"
      data-contrast="dark"
      className="premium-dark-surface flex h-full min-h-0 min-w-0 flex-col bg-[#171715] text-[#f7f5ef]"
    >
      <header
        style={
          isCoach
            ? undefined
            : { paddingTop: "max(12px, env(safe-area-inset-top, 0px))" }
        }
        className="flex shrink-0 items-center gap-3 border-b border-white/15 px-4 py-4 [@media(max-height:600px)]:py-2"
      >
        <button
          type="button"
          onClick={onBack}
          className="flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm"
          aria-label={isCoach ? "Back to messages" : "Back to membership"}
        >
          <ArrowLeft className="size-5" aria-hidden="true" />
          <span>Back</span>
        </button>
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-[#f0d49a]">
            Account support
          </p>
          <h1 className="truncate text-xl font-bold">
            {isCoach ? clientName || "Client" : "Coach Fe"}
          </h1>
        </div>
        {onSignOut && (
          <button
            type="button"
            onClick={onSignOut}
            disabled={signingOut}
            className="ml-auto min-h-11 shrink-0 px-2 text-xs text-[#c8c5bd] underline"
          >
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        )}
        <Shield
          className="ml-auto size-5 shrink-0 text-[#f0d49a]"
          aria-hidden="true"
        />
      </header>
      <div className="shrink-0 border-b border-white/15 px-5 py-3 text-sm leading-6 [@media(max-height:600px)]:py-2 [@media(max-height:600px)]:leading-5 text-[#c8c5bd]">
        <span className="[@media(max-height:600px)]:hidden">
          {isCoach
            ? "Account support only. Replying here does not change this client’s membership or access."
            : "Your app access remains paused. You can message Coach Fe here about your account. Training, community, and other messages stay locked."}
        </span>
        <span className="hidden [@media(max-height:600px)]:inline">
          {isCoach
            ? "Account support only. Access is unchanged."
            : "Access is paused. Message Coach Fe for account help."}
        </span>
        {onReviewMembership && (
          <button
            type="button"
            onClick={onReviewMembership}
            className="mt-2 block min-h-11 font-semibold [@media(max-height:600px)]:mt-0 [@media(max-height:600px)]:min-h-9 text-[#8fc2d8] underline underline-offset-4"
          >
            Review membership
          </button>
        )}
      </div>
      <div
        ref={scrollArea}
        onScroll={() => {
          const el = scrollArea.current;
          if (el)
            followLatest.current =
              el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-5"
        style={{ maxHeight: "55dvh" }}
      >
        {thread.isLoading && (
          <p
            role="status"
            className="flex items-center justify-center gap-2 py-10 text-[#c8c5bd]"
          >
            <Loader2 className="size-5 animate-spin" aria-hidden="true" />
            Loading account support
          </p>
        )}
        {thread.isError && (
          <div
            role="alert"
            className="mb-4 rounded-xl border border-white/20 p-4 text-sm"
          >
            <p>
              We could not load account support. Check your connection and try
              again.
            </p>
            <button
              type="button"
              onClick={() => void thread.refetch()}
              disabled={thread.isFetching}
              className="mt-2 flex min-h-11 items-center gap-2 text-[#8fc2d8]"
            >
              <RefreshCw className="size-4" aria-hidden="true" />
              Try again
            </button>
          </div>
        )}
        {!thread.isLoading && !thread.isError && messages.length === 0 && (
          <p className="py-8 text-center text-sm text-[#c8c5bd]">
            {isCoach
              ? "No account support messages yet."
              : "Need help with your account? Send Coach Fe a message below."}
          </p>
        )}
        <ol aria-label="Support messages" className="space-y-4">
          {messages.map((message) => {
            const own = message.fromCoach === isCoach;
            return (
              <li
                key={message.id}
                className={`flex ${own ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[88%] rounded-2xl border px-4 py-3 ${own ? "border-[#547a89] bg-[#223a45]" : "border-white/15 bg-[#232320]"}`}
                >
                  <p className="mb-1 text-xs font-semibold text-[#c8c5bd]">
                    {own
                      ? "You"
                      : message.fromCoach
                        ? "Coach Fe"
                        : clientName || "Client"}
                  </p>
                  <p className="whitespace-pre-wrap break-words text-sm leading-6 [overflow-wrap:anywhere]">
                    {message.content}
                  </p>
                  <time
                    dateTime={new Date(message.createdAt).toISOString()}
                    className="mt-2 block text-[11px] text-[#c8c5bd]"
                  >
                    {new Date(message.createdAt).toLocaleString([], {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </time>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
      <form
        onSubmit={handleSend}
        className="max-h-[50dvh] shrink-0 overflow-y-auto border-t border-white/15 p-4 [@media(max-height:600px)]:p-3"
        style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom, 0px))" }}
      >
        <label
          htmlFor="account-support-message"
          className="mb-2 block text-sm font-semibold"
        >
          {isCoach ? "Reply about this account" : "Message Coach Fe"}
        </label>
        <textarea
          id="account-support-message"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setSent(false);
          }}
          maxLength={4000}
          rows={3}
          disabled={isSending}
          placeholder="Write your message…"
          aria-describedby="account-support-message-limit"
          className="w-full resize-y rounded-xl [@media(max-height:600px)]:h-16 [@media(max-height:600px)]:min-h-0 [@media(max-height:600px)]:resize-none border border-white/20 bg-[#232320] px-3 py-3 text-base text-[#f7f5ef] placeholder:text-[#aaa79f] focus:outline-none focus:ring-2 focus:ring-[#8fc2d8] disabled:opacity-60"
        />
        {sendError && (
          <p role="alert" className="mt-2 text-sm text-[#f0d49a]">
            {sendError}
          </p>
        )}
        {sent && (
          <p role="status" className="mt-2 text-sm text-[#c8c5bd]">
            Message sent
          </p>
        )}
        <div className="mt-2 flex items-center justify-between gap-3">
          <p
            id="account-support-message-limit"
            className="text-xs text-[#c8c5bd]"
          >
            Text only · {draft.length}/4000
          </p>
          <button
            type="submit"
            disabled={
              isSending || !draft.trim() || thread.isError || !thread.data
            }
            className="flex min-h-11 items-center gap-2 rounded-xl bg-[#8fc2d8] px-5 py-3 text-sm font-bold text-[#171715] disabled:opacity-50"
          >
            {isSending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <Send className="size-4" aria-hidden="true" />
            )}
            {isSending ? "Sending…" : "Send message"}
          </button>
        </div>
      </form>
    </section>
  );
}
